import React, { useCallback, useEffect, useMemo } from 'react'
import { Form, Row, Spin } from 'antd'
import { isEmpty } from 'lodash'

import {
  DirtyPromptForm,
  FormButtons,
  FormImageUpload,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'

import { useUpdateCourseTypeImageDetailsMutation } from '../../hooks'
import { courseTypePropType } from '../../prop-types'

import { courseTypeImageFormInitialValues } from './initial-values'

const CourseTypeImageDetailsForm = ({ courseType }) => {
  const [form] = Form.useForm()

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.COURSE_TYPE.UPDATE_IMAGE_DETAILS,
  ])

  const [
    updateCourseTypeImageDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateCourseTypeImageDetailsMutation()

  const initialValues = useMemo(
    () => courseTypeImageFormInitialValues(courseType),
    [courseType],
  )

  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const handleSaveData = useCallback(
    data => updateCourseTypeImageDetails(courseType.id, data),
    [courseType.id, updateCourseTypeImageDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Course Type details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [courseType.imageUrl, resetForm])

  return isSaveLoading || isEmpty(initialValues) ? (
    <Spin />
  ) : (
    <>
      <DirtyPromptForm
        isDirty={isDirty}
        form={form}
        initialValues={initialValues}
        name="courseTypeImageForm"
        onFinish={handleSaveData}
        autoComplete="off"
        onFieldsChange={setToDirty}
      >
        <Row>
          <FormImageUpload
            disabled={!isFormEnabled}
            labelAbove
            name="image"
            label="Image"
          />
        </Row>
        <FormButtons
          permissions={[PERMISSIONS.COURSE_TYPE.UPDATE_IMAGE_DETAILS]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
      </DirtyPromptForm>
    </>
  )
}

CourseTypeImageDetailsForm.propTypes = {
  courseType: courseTypePropType.isRequired,
}

export default CourseTypeImageDetailsForm
